import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import ApprovalWorkflowForm from './components/ApprovalWorkflowForm';
import ViewWorkflow from './components/ViewWorkflow';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { fetchWorkflows, deleteWorkflow, updateWorkflow, createWorkflow } from './api/workflowApi';
import 'react-quill/dist/quill.snow.css';

const ApprovalWorkflow = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWorkflows();
      setWorkflows(
        data
          .filter(wf => wf && typeof wf === 'object')
          .map(wf => ({
            ...wf,
            settings: Array.isArray(wf.settings) ? wf.settings : []
          }))
      );
    } catch (error) {
      console.error('Error loading workflows:', error);
      showAlert('danger', 'Failed to load workflows.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const columns = [
    {
      key: 'workflowName',
      label: 'Workflow Name',
      width: '200px',
      className: 'text-truncate',
      render: (workflowName) => (
        <span 
          title={workflowName} 
          style={{ 
            display: 'inline-block', 
            maxWidth: '200px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}
        >
          {workflowName}
        </span>
      )
    },
    {
      key: 'service_id',
      label: 'Service ID',
      width: '40px',
      className: 'text-truncate',
      render: (serviceId) => (
        <span 
          title={serviceId} 
          style={{ 
            display: 'inline-block', 
            maxWidth: '40px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}
        >
          {serviceId}
        </span>
      )
    },
    {
      key: 'settings',
      label: 'Initiator',
      width: '300px',
      className: 'text-truncate',
      render: (settings, row) => {
        if (!Array.isArray(settings)) return '-';
        const initiator = settings.find(s => s.rule === 'initiator');
        if (!initiator || !Array.isArray(initiator.users) || initiator.users.length === 0) return '-';
        
        const users = initiator.users.sort((a, b) => {
          const nameA = (a.username || a.email || '').toLowerCase();
          const nameB = (b.username || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        const maxVisible = 3;
        const visibleUsers = users.slice(0, maxVisible);
        const remainingCount = users.length - maxVisible;
        const rowId = row.id || JSON.stringify(row);
        const isExpanded = expandedRows[rowId];
        const displayUsers = isExpanded ? users : visibleUsers;
        
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px', alignItems: 'center' }}>
            {displayUsers.map((user, index) => (
              <span 
                key={index}
                className="badge bg-primary text-white me-1 mb-1"
                title={user.username || user.email}
                style={{ 
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                {user.username || user.email}
              </span>
            ))}
            {remainingCount > 0 && !isExpanded && (
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                style={{ 
                  fontSize: '0.7rem', 
                  padding: '2px 6px',
                  minWidth: 'auto',
                  height: '20px',
                  lineHeight: '1'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(rowId);
                }}
              >
                +{remainingCount}
              </button>
            )}
            {remainingCount > 0 && isExpanded && (
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                style={{ 
                  fontSize: '0.7rem', 
                  padding: '2px 6px',
                  minWidth: 'auto',
                  height: '20px',
                  lineHeight: '1'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(rowId);
                }}
              >
                Show Less
              </button>
            )}
          </div>
        );
      }
    },
    {
      key: 'settings',
      label: 'Approver',
      width: '300px',
      className: 'text-truncate',
      render: (settings, row) => {
        if (!Array.isArray(settings)) return '-';
        const approver = settings.find(s => s.rule === 'approver');
        if (!approver || !Array.isArray(approver.users) || approver.users.length === 0) return '-';
        
        const users = approver.users.sort((a, b) => {
          const nameA = (a.username || a.email || '').toLowerCase();
          const nameB = (b.username || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        const maxVisible = 3;
        const visibleUsers = users.slice(0, maxVisible);
        const remainingCount = users.length - maxVisible;
        const rowId = `approver-${row.id || JSON.stringify(row)}`;
        const isExpanded = expandedRows[rowId];
        const displayUsers = isExpanded ? users : visibleUsers;
        
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px', alignItems: 'center' }}>
            {displayUsers.map((user, index) => (
              <span 
                key={index}
                className="badge bg-success text-white me-1 mb-1"
                title={user.username || user.email}
                style={{ 
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                {user.username || user.email}
              </span>
            ))}
            {remainingCount > 0 && !isExpanded && (
              <button
                className="btn btn-sm btn-outline-success"
                type="button"
                style={{ 
                  fontSize: '0.7rem', 
                  padding: '2px 6px',
                  minWidth: 'auto',
                  height: '20px',
                  lineHeight: '1'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(rowId);
                }}
              >
                +{remainingCount}
              </button>
            )}
            {remainingCount > 0 && isExpanded && (
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                style={{ 
                  fontSize: '0.7rem', 
                  padding: '2px 6px',
                  minWidth: 'auto',
                  height: '20px',
                  lineHeight: '1'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(rowId);
                }}
              >
                Show Less
              </button>
            )}
          </div>
        );
      }
    }
  ];

  // Dummy data for workflow approval list view
  const templateData = [
    {
      id: 1,
      workflowName: 'Project Proposal Approval',
      service: 'ISO 27001 Services',
      settings: [
        {
          step: 1,
          role: 'Project Manager',
          moveTo: '2',
          rule: 'initiator'
        },
        {
          step: 2,
          role: 'Department Head',
          moveTo: '3',
          rule: 'approver'
        },
        {
          step: 3,
          role: 'Director',
          moveTo: '',
          rule: 'final approver'
        }
      ]
    },
    {
      id: 2,
      workflowName: 'Quality Assessment Review',
      service: 'ISO 9001 Services',
      settings: [
        {
          step: 1,
          role: 'Quality Analyst',
          moveTo: '2',
          rule: 'initiator'
        },
        {
          step: 2,
          role: 'Quality Manager',
          moveTo: '3',
          rule: 'reviewer'
        },
        {
          step: 3,
          role: 'Quality Director',
          moveTo: '',
          rule: 'final approver'
        }
      ]
    }
  ];

  const handleFormSubmit = async (formData) => {
    setErrorMessage('');
    try {
      if (selectedWorkflow) {
        await updateWorkflow(selectedWorkflow.id, formData);
        showAlert('success', 'Workflow updated successfully!');
      } else {
        await createWorkflow(formData);
        showAlert('success', 'Workflow created successfully!');
      }
      await loadWorkflows();
      setIsModalOpen(false);
      setSelectedWorkflow(null);
    } catch (error) {
      let msg = 'Error saving workflow';
      if (error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      } else if (error.message) {
        msg = error.message;
      }
      setErrorMessage(msg);
      showAlert('danger', msg);
      console.error('Error saving workflow:', error);
    }
  };

  // Add these new handler functions
  const handleEdit = (row) => {
    setSelectedWorkflow(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleView = (row) => {
    setSelectedWorkflow(row);
    setIsViewModalOpen(true);
  };

  // Filter data berdasarkan searchQuery
  const filteredWorkflows = workflows.filter(wf => {
    const searchString = [
      wf.workflowName,
      wf.service_id,
      (wf.settings || []).map(s => s.users ? s.users.map(u => u.username).join(' ') : '').join(' ')
    ].join(' ').toLowerCase();

    return searchString.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Workflow Approval Rule</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      <ApprovalWorkflowForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedWorkflow(null);
          setErrorMessage('');
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedWorkflow}
      />
      {errorMessage && isModalOpen && (
        <div className="alert alert-danger" style={{ position: 'fixed', top: 80, left: 0, right: 0, zIndex: 2000, textAlign: 'center' }}>
          {errorMessage}
        </div>
      )}

      <ViewWorkflow
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedWorkflow(null);
        }}
        data={selectedWorkflow}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteWorkflow(itemToDelete.id);
            showAlert('success', 'Workflow deleted successfully!');
            await loadWorkflows();
          } catch (error) {
            console.error('Error deleting workflow:', error);
            showAlert('danger', 'Failed to delete workflow.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Workflow"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={filteredWorkflows}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search workflows..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalWorkflow; 